import './styles/main.scss';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {useEffect, useMemo, useState} from "react";
import InstallPage from "./pages/InstallPage";
import MainPage from "./pages/MainPage";
import RefreshResumePage from "./pages/RefreshResumePage";
import ChatPage from "./pages/ChatPage";
import Loading from "./elements/Loading";
import HhAuthorizePage from "./pages/HhAuthorizePage";
import HhChatAuthorizePage from "./pages/HhChatAuthorizePage";
import VacancySettingsPage from "./pages/VacancySettingsPage";
import ApplicantAnalysPage from "./pages/ApplicantAnalysPage";
import ResumeWorkspacePage from "./pages/ResumeWorkspacePage";
import getAPIClient from "./hooks/apiClient";

// Вкладка «зайти как портал»: админ держит открытыми несколько порталов сразу,
// поэтому вкладка подписывается хостом и получает админскую фавиконку — иначе
// вкладки неразличимы. В обычном режиме виджет живёт в iframe Bitrix, где ни
// title, ни favicon на вкладку не влияют, поэтому трогаем их только здесь.
const ADMIN_FAVICON = '/logo_square.png'

// Ссылка из админки одноразовая и живёт минуты — сама по себе доступа не даёт,
// её нужно обменять на сессионный токен. StrictMode в dev вызывает useMemo дважды,
// а второй обмен той же ссылки получит отказ, поэтому держим один промис на вкладку.
let linkExchange = null

function exchangeImpersonationLink(linkToken) {
    if (!linkExchange) {
        linkExchange = fetch(`${process.env.REACT_APP__API_URL}/admin/impersonate/exchange`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token: linkToken}),
        }).then(async response => {
            if (!response.ok) {
                const body = await response.json().catch(() => null)
                throw new Error(body?.detail || 'Не удалось открыть портал по этой ссылке.')
            }
            const data = await response.json()
            sessionStorage.setItem('impersonation_token', data.token)
            return data.token
        })
    }
    return linkExchange
}

function applyAdminTabIdentity(portalHost) {
    if (portalHost) {
        document.title = portalHost
    }
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
    }
    link.href = ADMIN_FAVICON
}

function App() {
    const [appInitialized, setAppInitialized] = useState(false)
    const [appLoaded, setAppLoaded] = useState(false)
    const [impersonationError, setImpersonationError] = useState(null)

    const BX24 = useMemo(() => {
        // Режим «зайти как портал» из админки: ссылку из URL меняем на сессионный
        // токен и прячем его в sessionStorage, адресную строку чистим сразу.
        // BX24 не инициализируем — авторизацию по токену собирает бэкенд
        const urlParams = new URLSearchParams(window.location.search)
        const linkToken = urlParams.get('impersonation_token')
        if (linkToken) {
            // Хост портала кладём отдельно: перезагрузка вкладки приходит уже
            // с чистым URL, а подпись вкладки должна пережить её
            const portalHost = urlParams.get('portal')
            if (portalHost) {
                sessionStorage.setItem('impersonation_portal', portalHost)
            }
            urlParams.delete('impersonation_token')
            urlParams.delete('portal')
            const qs = urlParams.toString()
            window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
            applyAdminTabIdentity(portalHost)
            exchangeImpersonationLink(linkToken)
                .then(() => setAppInitialized(true))
                .catch(e => setImpersonationError(e.message))
            return
        }
        // Ссылка уже обменяна раньше в этой вкладке — работаем сессионным токеном
        if (sessionStorage.getItem('impersonation_token')) {
            applyAdminTabIdentity(sessionStorage.getItem('impersonation_portal'))
            setAppInitialized(true)
            return
        }
        if (
            window.location.pathname.includes('hh_authorize') ||
            window.location.pathname.includes('hh_chat_authorize')
        ){
            setAppInitialized(true)
            return
        }
        const bx = window.BX24
        bx.init(() => setAppInitialized(true));
        return bx;
    }, [])

    useEffect(() => {
        if (!appInitialized) {
            return
        }
        if (
            window.location.pathname.includes('hh_authorize') ||
            window.location.pathname.includes('hh_chat_authorize') ||
            window.location.pathname.includes('install')
        ){
            setAppLoaded(true)
            return
        }
        const api = getAPIClient(BX24)
        api.get('/bitrix_portals/settings').then(() => {setAppLoaded(true)})
    }, [appInitialized])

    // Ссылка просрочена или уже использована: без этого экрана вкладка висела бы
    // в вечном лоадере, и было бы непонятно, что делать
    if (impersonationError) {
        return (
            <div style={{
                padding: '48px 24px', textAlign: 'center',
                fontFamily: 'sans-serif', color: '#374151'
            }}>
                <div style={{fontSize: '16px', marginBottom: '8px'}}>{impersonationError}</div>
                <div style={{fontSize: '13px', color: '#6b7280'}}>
                    Ссылка на вход одноразовая - откройте портал из админки заново.
                </div>
            </div>
        )
    }
    if (!appLoaded) {
        return <Loading isLoading={true}/>
    }
    const isImpersonation = Boolean(sessionStorage.getItem('impersonation_token'))
    // Хоста может не быть: ссылка выписана до появления параметра portal
    const impersonationPortal = sessionStorage.getItem('impersonation_portal')
    return (
        <BrowserRouter>
            {isImpersonation && (
                <div style={{
                    position: 'sticky', top: 0, zIndex: 1000,
                    background: '#7e22ce', color: '#fff', padding: '6px 14px',
                    fontSize: '13px', textAlign: 'center', fontFamily: 'sans-serif'
                }}>
                    Режим администратора ResumeScan - вы управляете приложением от имени портала
                    {impersonationPortal ? ` ${impersonationPortal}` : ''}
                </div>
            )}
            <Routes>
                <Route exact path="/" element={<MainPage BX24={BX24}/>} />
                <Route exact path="/hh_authorize" element={<HhAuthorizePage/>} />
                <Route exact path="/install" element={<InstallPage BX24={BX24}/>} />
                <Route exact path="/update_crm_item" element={<RefreshResumePage BX24={BX24}/>} />
                <Route exact path="/vacancy_settings" element={<VacancySettingsPage BX24={BX24}/>} />
                <Route exact path="/applicant_analys" element={<ApplicantAnalysPage BX24={BX24}/> } />
                <Route exact path="/resume_workspace" element={<ResumeWorkspacePage BX24={BX24}/> } />
                <Route exact path="/resume_selection" element={<ResumeWorkspacePage BX24={BX24}/> } />
                <Route exact path="/resume_dashboard" element={<ResumeWorkspacePage BX24={BX24}/> } />
                <Route exact path="/chat" element={<ChatPage BX24={BX24}/>} />
                <Route exact path="/hh_chat_authorize" element={<HhChatAuthorizePage/>} />
            </Routes>
        </BrowserRouter>
    )
}

export default App;
